import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import ReactDOM from 'react-dom';
import { WithCommonProps } from '@types';
import { zIndex } from '@utils/constants';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
export interface IDayPickerTooltipProps {
  /** Content to be rendered inside the tooltip */
  children: React.ReactNode;
  /** X coordinate for tooltip position */
  x: number;
  /** Y coordinate for tooltip position */
  y: number;
  /** Indicates whether the tooltip is visible */
  isVisible: boolean;
}
export interface IDayPickerTooltipStateProps {
  /** Function to get tooltip content for a day */
  getTooltipContent: (day: moment.Moment) => string | undefined;
  /** Render function for day contents */
  renderDayContents: (day: moment.Moment, modifiers: Set<string>) => React.ReactNode;
}
export interface IDayPickerTooltipContentProps {
  /** The day to render tooltip content for */
  day: moment.Moment;
  /** Function to get minimum nights for a hover date */
  getMinNightsForHoverDate: (day: moment.Moment) => number;
  /** Check-in date */
  checkInDate: string | null;
  /** Check-out date */
  checkOutDate: string | null;
  /** Function to check if start day is blocked */
  isStartDayBlocked: (day: moment.Moment) => boolean;
  /** Function to check if end day is blocked */
  isEndDayBlocked: (day: moment.Moment) => boolean;
  /** Function to check if day is blocked */
  isDayBlocked: (day: moment.Moment) => boolean;
}
export const getDayPickerTooltipContent = ({
  day,
  getMinNightsForHoverDate,
  checkInDate,
  checkOutDate,
  isStartDayBlocked,
  isEndDayBlocked,
  isDayBlocked
}: IDayPickerTooltipContentProps & {
  isDayBlocked: (day: moment.Moment) => boolean;
}): string | undefined => {
  if (!isStartDayBlocked(day) && !isEndDayBlocked(day) && !isDayBlocked(day) && !checkInDate && getMinNightsForHoverDate(day) > 1) {
    return `${getMinNightsForHoverDate(day)}-night minimum`;
  }
  if (!checkInDate && isStartDayBlocked(day)) {
    return 'Checkout only';
  }
  if (checkInDate && !checkOutDate && !isDayBlocked(day) && isEndDayBlocked(day)) {
    return 'Check-in only';
  }
  // Show minimum stay requirement when hovering over days that can't be selected due to check-in date's minimum stay
  if (checkInDate && !checkOutDate && !isDayBlocked(day) && !isEndDayBlocked(day)) {
    const checkInMoment = moment(checkInDate);
    const minNightsForCheckIn = getMinNightsForHoverDate(checkInMoment);
    const daysFromCheckIn = day.diff(checkInMoment, 'days');

    // Only show minimum stay tooltip for dates after the check-in date that are less than the minimum nights
    if (daysFromCheckIn > 0 && daysFromCheckIn < minNightsForCheckIn) {
      return `${minNightsForCheckIn}-night minimum`;
    }
  }
  return undefined;
};
export const useDayPickerTooltip = getTooltipContent => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      content: "",
      x: 0,
      y: 0,
      isVisible: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [tooltipState, setTooltipState] = React.useState(t0);
  const tooltipTimeoutRef = React.useRef(null);
  let t1;
  let t2;
  if ($[1] !== tooltipState.isVisible) {
    t1 = () => {
      const handleMouseOver = e => {
        const target = e.target as Element | null;
        if (!target?.getAttribute) {
          return;
        }
        const tooltipContent = target.getAttribute("data-day-picker-tooltip-content");
        if (tooltipContent && tooltipContent.trim()) {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
          const rect = target.getBoundingClientRect();
          setTooltipState({
            content: tooltipContent,
            x: rect.left + rect.width / 2,
            y: rect.top - 40,
            isVisible: true
          });
        }
      };
      const handleMouseOut = e_0 => {
        const target_0 = e_0.target as Element | null;
        if (!target_0?.getAttribute) {
          return;
        }
        const tooltipContent_0 = target_0.getAttribute("data-day-picker-tooltip-content");
        if (tooltipContent_0 && tooltipContent_0.trim()) {
          tooltipTimeoutRef.current = setTimeout(() => {
            setTooltipState(_temp);
          }, 50);
        }
      };
      const handleClick = e_1 => {
        const target_1 = e_1.target as Element | null;
        if (!target_1?.getAttribute) {
          return;
        }
        const tooltipContent_1 = target_1.getAttribute("data-day-picker-tooltip-content");
        if (tooltipContent_1 && tooltipContent_1.trim()) {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
          setTooltipState(_temp2);
        }
      };
      const handleMouseMove = e_2 => {
        const target_2 = e_2.target as Element | null;
        if (!target_2?.getAttribute) {
          return;
        }
        const tooltipContent_2 = target_2.getAttribute("data-day-picker-tooltip-content");
        if (!tooltipContent_2 && tooltipState.isVisible) {
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
          setTooltipState(_temp3);
        }
      };
      document.addEventListener("mouseover", handleMouseOver);
      document.addEventListener("mouseout", handleMouseOut);
      document.addEventListener("click", handleClick);
      document.addEventListener("mousemove", handleMouseMove);
      return () => {
        document.removeEventListener("mouseover", handleMouseOver);
        document.removeEventListener("mouseout", handleMouseOut);
        document.removeEventListener("click", handleClick);
        document.removeEventListener("mousemove", handleMouseMove);
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
      };
    };
    t2 = [tooltipState.isVisible];
    $[1] = tooltipState.isVisible;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[4] !== getTooltipContent) {
    t3 = (day, _modifiers) => {
      const tooltipContent_3 = getTooltipContent(day);
      return <div data-day-picker-tooltip-content={tooltipContent_3 || ""} data-day-timestamp={day.valueOf()} className="w-full h-full flex items-center justify-center">{day.format("D")}</div>;
    };
    $[4] = getTooltipContent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const renderDayContents = t3;
  let t4;
  if ($[6] !== renderDayContents || $[7] !== tooltipState) {
    t4 = {
      tooltipState,
      renderDayContents
    };
    $[6] = renderDayContents;
    $[7] = tooltipState;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export const DayPickerTooltip: React.FC<WithCommonProps<IDayPickerTooltipProps>> = t0 => {
  const $ = _c(23);
  let children;
  let classNameProp;
  let isVisible;
  let rest;
  let x;
  let y;
  if ($[0] !== t0) {
    ({
      children,
      x,
      y,
      isVisible,
      className: classNameProp,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = classNameProp;
    $[3] = isVisible;
    $[4] = rest;
    $[5] = x;
    $[6] = y;
  } else {
    children = $[1];
    classNameProp = $[2];
    isVisible = $[3];
    rest = $[4];
    x = $[5];
    y = $[6];
  }
  let t1;
  if ($[7] !== classNameProp) {
    t1 = twMerge("inline-block bg-white text-primary-main border border-primary-dark p-2 rounded-lg whitespace-nowrap max-w-[500px] break-words overflow-hidden font-sans !font-[Roobert] text-xs", classNameProp);
    $[7] = classNameProp;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const tooltipClassName = t1;
  if (!isVisible) {
    return null;
  }
  let t2;
  if ($[9] !== children || $[10] !== rest || $[11] !== tooltipClassName || $[12] !== x || $[13] !== y) {
    let t3;
    if ($[15] !== x || $[16] !== y) {
      t3 = {
        position: "fixed",
        left: x,
        top: y,
        transform: "translateX(-50%)",
        zIndex: zIndex.tooltip
      };
      $[15] = x;
      $[16] = y;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    let t4;
    if ($[18] !== children || $[19] !== rest || $[20] !== t3 || $[21] !== tooltipClassName) {
      t4 = <div style={t3} className={tooltipClassName} {...rest}>{children}</div>;
      $[18] = children;
      $[19] = rest;
      $[20] = t3;
      $[21] = tooltipClassName;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    t2 = ReactDOM.createPortal(t4, document.body);
    $[9] = children;
    $[10] = rest;
    $[11] = tooltipClassName;
    $[12] = x;
    $[13] = y;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
function _temp(prev) {
  return {
    ...prev,
    isVisible: false
  };
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    isVisible: false
  };
}
function _temp3(prev_1) {
  return {
    ...prev_1,
    isVisible: false
  };
}